/**
 * I `meta.warnings` che arrivano sulle risposte **riuscite**.
 *
 * ## Il buco che questo file chiude
 *
 * Il BE ha un pattern trasversale (D-X1): quando un'operazione va a buon fine ma qualcosa di
 * accessorio non è riuscito, la risposta resta **2xx** e porta l'avviso in `meta.warnings`.
 * L'esempio che ha fatto emergere il problema è `ORDER_NEXT_PROJECT_TEAM_COPY_FAILED`: la riga
 * d'ordine passa a `consumed` — e quella transizione resta — ma la copia del team sulla
 * commessa successiva fallisce. Nessuno viene assegnato alla commessa nuova, e non c'è niente
 * a schermo che lo dica: il buco si scopre quando qualcuno prova a rendicontarci e non risulta
 * nel team.
 *
 * `createOne`/`updateOne` **denormalizzano e buttano via il `meta` di primo livello**. Ogni
 * avviso di questo tipo, su qualunque risorsa, finora è caduto nel vuoto — non solo questo.
 *
 * ## Perché un canale unico e non un parametro per chiamata
 *
 * Aggiungere una callback a ogni `createOne` significherebbe ricordarsene su ogni nuova
 * scrittura: la classe di difetto tornerebbe alla prima dimenticanza, in silenzio come prima.
 * Qui la raccolta è nel client — una volta sola, per tutte le risorse — e la resa è di chi
 * registra l'handler. È lo stesso disegno di `setAuthHeadersProvider`: il layer HTTP non sa
 * cosa farne, sa solo che qualcuno deve saperlo.
 */
import type { JsonApiDocument } from './types';

/** Forma di un avviso, identica a quella dei workflow endpoint (`shared/api/workflow.ts`). */
export interface JsonApiWarning {
  code: string;
  message?: string;
  /** Dati del caso: `salesOrderLineId`, contatori, id correlati. */
  params?: Record<string, unknown>;
}

/** Da dove viene l'avviso: serve a chi lo mostra per dire *su cosa* è successo. */
export interface WarningContext {
  /** Tipo JSON:API della risorsa scritta, es. `timesheet-entries`. */
  resourceType: string;
  /** Id della risorsa, quando è noto. */
  id?: string;
  operation: 'create' | 'update';
}

type WarningHandler = (warnings: JsonApiWarning[], ctx: WarningContext) => void;

let handler: WarningHandler | null = null;

/**
 * Registra chi mostra gli avvisi. Uno solo: due handler significherebbero due notifiche per
 * lo stesso fatto. Passare `null` disattiva la resa senza toccare la raccolta.
 */
export function setJsonApiWarningHandler(h: WarningHandler | null): void {
  handler = h;
}

/**
 * Estrae gli avvisi da un documento JSON:API.
 *
 * Tollera le due forme in cui il BE li scrive — oggetti `{code, message}` e stringhe secche —
 * perché il pattern è trasversale e non tutti i controller lo confezionano allo stesso modo.
 * Le voci senza un `code` leggibile vengono scartate: un avviso che non si può nemmeno
 * nominare non è mostrabile, e inventargli un'etichetta sarebbe peggio del silenzio.
 */
export function extractWarnings(doc: JsonApiDocument | undefined | null): JsonApiWarning[] {
  const raw = (doc?.meta as { warnings?: unknown } | undefined)?.warnings;
  if (!Array.isArray(raw)) return [];
  const out: JsonApiWarning[] = [];
  for (const w of raw) {
    // `params` sempre valorizzato, anche vuoto: un consumatore che legge
    // `w.params.salesOrderLineId` non deve dover indovinare quale forma è arrivata.
    if (typeof w === 'string' && w.trim()) { out.push({ code: w.trim(), params: {} }); continue; }
    if (!w || typeof w !== 'object') continue;
    const o = w as Record<string, unknown>;
    const code = typeof o.code === 'string' ? o.code : undefined;
    if (!code) continue;
    out.push({
      code,
      message: typeof o.message === 'string' ? o.message : typeof o.detail === 'string' ? o.detail : undefined,
      // I dati del caso stanno in `params` o direttamente nell'oggetto (i controller divergono):
      // si tiene tutto tranne le due chiavi già lette, così `salesOrderLineId` arriva comunque.
      params: (o.params && typeof o.params === 'object' ? o.params as Record<string, unknown> : rest(o)),
    });
  }
  return out;
}

function rest(o: Record<string, unknown>): Record<string, unknown> {
  const { code: _c, message: _m, detail: _d, ...other } = o;
  return other;
}

/** Raccoglie e consegna. Nessun handler registrato = nessuna resa, e nessun errore. */
export function emitJsonApiWarnings(doc: JsonApiDocument | undefined | null, ctx: WarningContext): void {
  if (!handler) return;
  const warnings = extractWarnings(doc);
  if (warnings.length === 0) return;
  try {
    handler(warnings, ctx);
  } catch {
    /* La resa di un avviso non deve mai far fallire la scrittura che l'ha prodotta: la
       scrittura è già andata a buon fine, e propagare qui trasformerebbe un 2xx in un errore. */
  }
}
